/**
 * Tournament Store - Zustand state management
 *
 * Multi-tournament support with user creation
 */

import { create } from 'zustand';
import tournamentService from '../services/tournamentService';

// =====================================================
// CONSTANTS
// =====================================================

export const TOURNAMENT_STATUS = {
    UPCOMING: 'upcoming',
    REGISTRATION: 'registration',
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
};

export const USER_TOURNAMENT_STATE = {
    NOT_REGISTERED: 'not_registered',
    REGISTERED: 'registered',
    ACTIVE: 'active',
    ELIMINATED: 'eliminated',
    CHAMPION: 'champion',
};

export const TOURNAMENT_CATEGORIES = [
    { value: 'geral', label: 'Geral' },
    { value: 'direito', label: 'Direito' },
    { value: 'portugues', label: 'Português' },
    { value: 'raciocinio', label: 'Raciocínio Lógico' },
    { value: 'informatica', label: 'Informática' },
    { value: 'administracao', label: 'Administração' },
    { value: 'contabilidade', label: 'Contabilidade' },
];

// Categorias extras, exibidas no minimodal do badge "Outra" (CreateTournamentScreen)
export const TOURNAMENT_CATEGORIES_EXTRA = [
    { value: 'programacao', label: 'Programação' },
    { value: 'enem', label: 'ENEM' },
    { value: 'atualidades', label: 'Atualidades' },
    { value: 'idiomas', label: 'Idiomas' },
    { value: 'matematica', label: 'Matemática' },
    { value: 'historia', label: 'História' },
    { value: 'geografia', label: 'Geografia' },
    { value: 'ciencias', label: 'Ciências' },
    { value: 'biologia', label: 'Biologia' },
    { value: 'fisica', label: 'Física' },
    { value: 'quimica', label: 'Química' },
    { value: 'filosofia', label: 'Filosofia' },
    { value: 'sociologia', label: 'Sociologia' },
    { value: 'literatura', label: 'Literatura' },
    { value: 'economia', label: 'Economia' },
    { value: 'medicina', label: 'Medicina' },
    { value: 'engenharia', label: 'Engenharia' },
    { value: 'militar', label: 'Concursos Militares' },
];

export const XP_REWARDS = {
    participation: 25,
    matchWin: 50,
    quarterFinal: 100,
    semiFinal: 200,
    runnerUp: 350,
    champion: 500,
};

// Backward-compatible alias
export const TOURNAMENT_REWARDS = XP_REWARDS;

// =====================================================
// STORE
// =====================================================

const useTournamentStore = create((set, get) => ({
    // Tournament lists
    openTournaments: [],
    inProgressTournaments: [],
    myTournaments: [],
    completedTournaments: [],

    // Currently selected tournament
    selectedTournament: null,
    selectedBracket: [],
    participants: [],

    // Match state
    currentMatch: null,
    matchScore: { player1: 0, player2: 0 },
    currentQuestionIndex: 0,
    matchQuestions: [],

    // UI states
    isLoading: false,
    isCreating: false,
    isRegistering: false,
    error: null,

    // Backward-compatible state (used by original TournamentHubScreen / TournamentCard)
    activeTournament: null,
    isRegistered: false,
    userState: USER_TOURNAMENT_STATE.NOT_REGISTERED,

    // =====================================================
    // FETCH TOURNAMENTS
    // =====================================================

    fetchOpenTournaments: async () => {
        try {
            const data = await tournamentService.getTournamentsByStatus('registration');
            set({ openTournaments: data });
        } catch (error) {
            console.error('[Tournament] Error fetching open:', error);
        }
    },

    fetchInProgressTournaments: async () => {
        try {
            const data = await tournamentService.getTournamentsByStatus('in_progress');
            set({ inProgressTournaments: data });
        } catch (error) {
            console.error('[Tournament] Error fetching in progress:', error);
        }
    },

    fetchMyTournaments: async (userId) => {
        try {
            const data = await tournamentService.getMyTournaments(userId);
            set({ myTournaments: data });
        } catch (error) {
            console.error('[Tournament] Error fetching my tournaments:', error);
        }
    },

    fetchCompletedTournaments: async () => {
        try {
            const data = await tournamentService.getTournamentHistory(20);
            set({ completedTournaments: data });
        } catch (error) {
            console.error('[Tournament] Error fetching completed:', error);
        }
    },

    fetchAllTournaments: async (userId) => {
        set({ isLoading: true, error: null });
        try {
            await Promise.all([
                get().fetchOpenTournaments(),
                get().fetchInProgressTournaments(),
                get().fetchMyTournaments(userId),
                get().fetchCompletedTournaments(),
            ]);
        } catch (error) {
            set({ error: error.message });
        } finally {
            set({ isLoading: false });
        }
    },

    // Backward-compatible: fetch ONE active tournament (first open or in-progress)
    fetchActiveTournament: async () => {
        set({ isLoading: true });
        try {
            // Try in-progress first, then open
            let data = await tournamentService.getTournamentsByStatus('in_progress');
            if (!data || data.length === 0) {
                data = await tournamentService.getTournamentsByStatus('registration');
            }
            const active = data && data.length > 0 ? data[0] : null;
            set({
                activeTournament: active,
                inProgressTournaments: active && active.status === 'in_progress' ? data : [],
                openTournaments: active && active.status === 'registration' ? data : [],
                isLoading: false,
            });
        } catch (error) {
            console.error('[Tournament] Error fetching active:', error);
            set({ isLoading: false });
        }
    },

    // =====================================================
    // SELECT TOURNAMENT
    // =====================================================

    selectTournament: async (tournamentId) => {
        set({ isLoading: true, error: null });
        try {
            const tournament = await tournamentService.getTournamentById(tournamentId);
            const participants = await tournamentService.getTournamentParticipants(tournamentId);

            let bracket = [];
            if (tournament.status === 'in_progress' || tournament.status === 'completed') {
                bracket = await tournamentService.getTournamentBracket(tournamentId);
            }

            set({
                selectedTournament: tournament,
                participants,
                selectedBracket: bracket,
                isLoading: false,
            });
        } catch (error) {
            set({ error: error.message, isLoading: false });
        }
    },

    // Busca SÓ os participantes. Existe para a sala de espera (bracket ainda não
    // sorteado): `selectTournament` traria torneio + participantes + bracket, e
    // ali o bracket é justamente o que não existe ainda.
    fetchParticipants: async (tournamentId) => {
        if (!tournamentId) return;
        try {
            const participants = await tournamentService.getTournamentParticipants(tournamentId);
            set({ participants });
        } catch (error) {
            console.error('[Tournament] Error fetching participants:', error);
        }
    },

    clearSelectedTournament: () => {
        set({ selectedTournament: null, selectedBracket: [], participants: [] });
    },

    // =====================================================
    // CREATE TOURNAMENT
    // =====================================================

    createTournament: async (tournamentData) => {
        set({ isCreating: true, error: null });
        try {
            // Check limit: max 3 active tournaments per user
            const activeCount = await tournamentService.getUserActiveTournamentCount(
                tournamentData.created_by
            );
            if (activeCount >= 3) {
                throw new Error('Você já tem 3 torneios ativos. Aguarde algum finalizar.');
            }

            // Generate tournament number
            const number = Date.now() % 100000;

            const data = await tournamentService.createTournament({
                ...tournamentData,
                number,
                status: 'registration',
            });

            // Add to the local lists
            set(state => ({
                openTournaments: [data, ...state.openTournaments],
                myTournaments: [data, ...state.myTournaments],
                isCreating: false,
            }));

            return { success: true, tournament: data };
        } catch (error) {
            set({ error: error.message, isCreating: false });
            return { success: false, error: error.message };
        }
    },

    cancelTournament: async (tournamentId, userId) => {
        try {
            await tournamentService.cancelTournament(tournamentId, userId);

            set(state => ({
                openTournaments: state.openTournaments.filter(t => t.id !== tournamentId),
                myTournaments: state.myTournaments.filter(t => t.id !== tournamentId),
                selectedTournament: state.selectedTournament?.id === tournamentId
                    ? null
                    : state.selectedTournament,
            }));

            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    startTournamentEarly: async (tournamentId, userId) => {
        try {
            const data = await tournamentService.startTournamentEarly(tournamentId, userId);

            set(state => ({
                openTournaments: state.openTournaments.filter(t => t.id !== tournamentId),
                inProgressTournaments: [data, ...state.inProgressTournaments],
                selectedTournament: state.selectedTournament?.id === tournamentId
                    ? { ...state.selectedTournament, status: 'in_progress' }
                    : state.selectedTournament,
            }));

            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    // =====================================================
    // REGISTRATION
    // =====================================================

    // Registration check (multi-tournament: tournamentId + userId)
    checkRegistrationForTournament: async (tournamentId, userId) => {
        try {
            const registration = await tournamentService.checkUserRegistration(tournamentId, userId);
            return registration ? registration.status : null;
        } catch (error) {
            console.error('[Tournament] Error checking registration:', error);
            return null;
        }
    },

    // Backward-compatible: check registration for active tournament using just userId
    checkRegistration: async (userId) => {
        const { activeTournament } = get();
        if (!activeTournament || !userId) {
            set({ isRegistered: false, userState: USER_TOURNAMENT_STATE.NOT_REGISTERED });
            return;
        }
        try {
            const registration = await tournamentService.checkUserRegistration(activeTournament.id, userId);
            const registered = !!registration;
            set({
                isRegistered: registered,
                userState: registered
                    ? (registration.status === 'active' ? USER_TOURNAMENT_STATE.ACTIVE
                        : registration.status === 'eliminated' ? USER_TOURNAMENT_STATE.ELIMINATED
                            : registration.status === 'champion' ? USER_TOURNAMENT_STATE.CHAMPION
                                : USER_TOURNAMENT_STATE.REGISTERED)
                    : USER_TOURNAMENT_STATE.NOT_REGISTERED,
            });
        } catch (error) {
            console.error('[Tournament] Error checking registration:', error);
            set({ isRegistered: false, userState: USER_TOURNAMENT_STATE.NOT_REGISTERED });
        }
    },

    // Multi-tournament register (tournamentId + userId)
    registerForTournament: async (tournamentId, userId) => {
        set({ isRegistering: true, error: null });
        try {
            await tournamentService.registerForTournament(tournamentId, userId);

            set(state => ({
                openTournaments: state.openTournaments.map(t =>
                    t.id === tournamentId
                        ? { ...t, participant_count: (t.participant_count || 0) + 1 }
                        : t
                ),
                selectedTournament: state.selectedTournament?.id === tournamentId
                    ? {
                        ...state.selectedTournament,
                        participant_count: (state.selectedTournament.participant_count || 0) + 1,
                    }
                    : state.selectedTournament,
                isRegistering: false,
            }));

            return { success: true };
        } catch (error) {
            set({ error: error.message, isRegistering: false });
            return { success: false, error: error.message };
        }
    },

    // Backward-compatible: register for active tournament using just userId
    register: async (userId) => {
        const { activeTournament } = get();
        if (!activeTournament) return { success: false, error: 'Nenhum torneio ativo' };

        set({ isRegistering: true, error: null });
        try {
            await tournamentService.registerForTournament(activeTournament.id, userId);

            set(state => ({
                activeTournament: state.activeTournament
                    ? {
                        ...state.activeTournament,
                        participant_count: (state.activeTournament.participant_count || 0) + 1,
                    }
                    : null,
                isRegistered: true,
                userState: USER_TOURNAMENT_STATE.REGISTERED,
                isRegistering: false,
            }));

            return { success: true };
        } catch (error) {
            set({ error: error.message, isRegistering: false });
            return { success: false, error: error.message };
        }
    },

    unregister: async (tournamentId, userId) => {
        set({ isRegistering: true });
        try {
            await tournamentService.unregisterFromTournament(tournamentId, userId);

            set(state => ({
                openTournaments: state.openTournaments.map(t =>
                    t.id === tournamentId
                        ? { ...t, participant_count: Math.max(0, (t.participant_count || 0) - 1) }
                        : t
                ),
                selectedTournament: state.selectedTournament?.id === tournamentId
                    ? {
                        ...state.selectedTournament,
                        participant_count: Math.max(0, (state.selectedTournament.participant_count || 0) - 1),
                    }
                    : state.selectedTournament,
                isRegistering: false,
            }));

            return { success: true };
        } catch (error) {
            set({ isRegistering: false });
            return { success: false, error: error.message };
        }
    },

    // =====================================================
    // MATCH
    // =====================================================

    fetchCurrentMatch: async (tournamentId, userId) => {
        try {
            const match = await tournamentService.getUserCurrentMatch(tournamentId, userId);
            if (match) {
                set({ currentMatch: match });
            }
            return match;
        } catch (error) {
            console.error('[Tournament] Error fetching match:', error);
            return null;
        }
    },

    // is_correct é decidido pelo SERVER (Edge Function submit-answer) — o
    // client manda só a opção escolhida e usa o retorno pra atualizar o placar
    submitAnswer: async (matchId, userId, questionIndex, selectedOption, timeMs) => {
        try {
            const result = await tournamentService.submitMatchAnswer(
                matchId, userId, questionIndex, selectedOption, timeMs
            );

            if (result?.is_correct) {
                set(state => {
                    const match = state.currentMatch;
                    if (!match) return state;

                    const isPlayer1 = userId === match.player1_id;
                    return {
                        matchScore: {
                            player1: isPlayer1
                                ? state.matchScore.player1 + 1
                                : state.matchScore.player1,
                            player2: !isPlayer1
                                ? state.matchScore.player2 + 1
                                : state.matchScore.player2,
                        },
                    };
                });
            }

            set(state => ({ currentQuestionIndex: state.currentQuestionIndex + 1 }));
            return { success: true, ...result };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    // Apuração é do backend (submit-answer chama process-match; janela fecha
    // via tick-tournament) — aqui só limpa o estado local da partida
    endMatch: () => {
        set({
            currentMatch: null,
            matchScore: { player1: 0, player2: 0 },
            currentQuestionIndex: 0,
            matchQuestions: [],
        });
        return { success: true };
    },

    // =====================================================
    // BRACKET
    // =====================================================

    fetchBracket: async (tournamentId) => {
        try {
            const bracket = await tournamentService.getTournamentBracket(tournamentId);
            set({ selectedBracket: bracket });
        } catch (error) {
            console.error('[Tournament] Error fetching bracket:', error);
        }
    },

    // =====================================================
    // RESET
    // =====================================================

    reset: () => {
        set({
            openTournaments: [],
            inProgressTournaments: [],
            myTournaments: [],
            completedTournaments: [],
            selectedTournament: null,
            selectedBracket: [],
            participants: [],
            currentMatch: null,
            matchScore: { player1: 0, player2: 0 },
            currentQuestionIndex: 0,
            matchQuestions: [],
            isLoading: false,
            isCreating: false,
            isRegistering: false,
            error: null,
        });
    },
}));

export default useTournamentStore;
